import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  useCreateExpense,
  useExpenseSubcategories,
  useExpenseTaxes,
  useGetTenantInformation,
} from '@pflegenavi/frontend/api-nursing-home';
import {
  createInternalId,
  draftsToUpdateParts,
  hasArchivedSubcategoryPart,
  hasZeroAmountPart,
  hasArchivedTaxPart,
} from './expensePartsDrafts';
import { ExpensePartsEditor, type PartDraft } from './ExpensePartsEditor';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import { YEAR_MONTH_DAY_HOUR_MINUTE_SHORT_FORMAT } from '@pflegenavi/shared/constants';
import { useSnackbar } from 'notistack';
import {
  FDatePicker,
  FTextInput,
  Iconify,
  Typography,
  useErrorSnackbar,
} from '@pflegenavi/web-components';
import { endOfToday, format } from 'date-fns';
import type { TFunction } from 'i18next';
import type { FC } from 'react';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  UploadReceiptCard,
  useUploadReceipt,
} from '@pflegenavi/transaction-components';
import { useFeatureFlagEnabled } from '@pflegenavi/frontend/feature-flags';
import { FeatureFlag } from '@pflegenavi/shared/api';
import {
  ReceiptAutofillAdornment,
  useReceiptAnalysisAutofill,
} from './useReceiptAnalysisAutofill';

interface FormValues {
  receiptImageIds: string[];
  receipt_not_available: boolean;
  receiptDate: Date | null;
  receiptNumber: string;
  comment: string;
  parts: PartDraft[];
}

export interface AssignToExpenseManagementModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Cash transaction group to assign — caller must ensure amount_linked === 0.
   * A function is called on confirm instead, so cancelling the dialog assigns
   * nothing; it resolves to null when creation failed (and was reported).
   */
  cashTransactionGroupId: string | (() => Promise<string | null>);
  /** Resets the form when the target changes; defaults to the group id. */
  resetKey?: string;
  /** Absolute group amount in cents */
  groupAmountCents: number;
  /** Shown in the context block at the top of the dialog. */
  merchantName?: string | null;
  /** Pliant cardholder name; when set, shown in the info block. */
  cardholderName?: string | null;
  /** Pliant transaction creation timestamp (ISO); when set, shown in the info block. */
  pliantTransactionCreatedAt?: string | null;
  description?: React.ReactNode;
  onSuccess?: () => void;
}

function buildValidationSchema(groupAmount: number, t: TFunction) {
  return Yup.object({
    receiptImageIds: Yup.array().of(Yup.string().required()),
    receipt_not_available: Yup.boolean().required(),
    receiptDate: Yup.date()
      .nullable()
      .max(endOfToday(), t('field.date-no-future'))
      .required(t('field.select-date')),
    receiptNumber: Yup.string(),
    comment: Yup.string(),
    parts: Yup.array().test(
      'parts-sum-matches-group',
      'parts-sum-mismatch',
      (parts) => sumPartAmounts(parts) === groupAmount
    ),
  });
}

function sumPartAmounts(parts: PartDraft[] | undefined): number {
  if (!parts) {
    return 0;
  }
  return parts.reduce((acc, p) => acc + (p.amount || 0), 0);
}

export const AssignToExpenseManagementModal: FC<
  AssignToExpenseManagementModalProps
> = ({
  open,
  onClose,
  cashTransactionGroupId,
  groupAmountCents,
  merchantName,
  cardholderName,
  pliantTransactionCreatedAt,
  description,
  onSuccess,
  resetKey = typeof cashTransactionGroupId === 'string'
    ? cashTransactionGroupId
    : '',
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  const fDate = useFormatDate();
  const { enqueueSnackbar } = useSnackbar();
  const { enqueueErrorSnackbar } = useErrorSnackbar();
  const { mutateAsync: createExpense } = useCreateExpense();
  const tenantInfo = useGetTenantInformation();

  const { data: subcategoriesData } = useExpenseSubcategories({
    enabled: open,
  });
  const { data: taxCodesData } = useExpenseTaxes({ enabled: open });

  const defaultReceiptDate = useMemo(
    () =>
      pliantTransactionCreatedAt ? new Date(pliantTransactionCreatedAt) : null,
    [pliantTransactionCreatedAt]
  );

  const initialValues = useMemo<FormValues>(
    () => ({
      receiptImageIds: [],
      receipt_not_available: false,
      receiptDate: defaultReceiptDate,
      receiptNumber: '',
      comment: '',
      parts: [
        {
          _internalId: createInternalId(),
          amount: groupAmountCents,
          expense_subcategory_id: null,
          expense_tax_id: null,
        },
      ],
    }),
    // Reset the form when the caller switches to a different target row.
    // resetKey isn't read in the body — it's here purely as a reset trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resetKey, groupAmountCents, defaultReceiptDate]
  );

  // A group created on confirm must survive a failed expense save, or the
  // retry would create a second one.
  const createdGroupIdRef = useRef<string | null>(null);
  useEffect(() => {
    createdGroupIdRef.current = null;
  }, [resetKey]);

  const validationSchema = useMemo(
    () => buildValidationSchema(groupAmountCents, t),
    [groupAmountCents, t]
  );

  const formik = useFormik<FormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (!values.receiptDate) {
        return;
      }
      try {
        let groupId: string | null;
        if (typeof cashTransactionGroupId === 'string') {
          groupId = cashTransactionGroupId;
        } else {
          createdGroupIdRef.current ??= await cashTransactionGroupId();
          groupId = createdGroupIdRef.current;
        }
        if (!groupId) {
          return;
        }
        await createExpense({
          body: {
            cash_list_transaction_group_id: groupId,
            expense_parts: draftsToUpdateParts(values.parts),
            comment:
              values.comment.trim() === '' ? null : values.comment.trim(),
            receipt_date: format(values.receiptDate, 'yyyy-MM-dd'),
            receipt_number:
              values.receiptNumber.trim() === ''
                ? null
                : values.receiptNumber.trim(),
            receipt_image_ids: values.receiptImageIds,
            receipt_not_available: values.receipt_not_available,
          },
        });

        enqueueSnackbar(
          t('receipts.unassociatedPliant.assignExpense.success'),
          { variant: 'success' }
        );
        onSuccess?.();
        onClose();
      } catch (error) {
        enqueueErrorSnackbar(error, t('errors.something-went-wrong'));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const uploadReceipt = useUploadReceipt({ formik });

  const receiptAnalysisEnabled = useFeatureFlagEnabled(
    FeatureFlag.ReceiptImageAnalysis
  );
  const analysis = useReceiptAnalysisAutofill({
    formik,
    enabled: receiptAnalysisEnabled,
    defaultReceiptDate,
    resetKey,
  });

  const partsSumMatches =
    sumPartAmounts(formik.values.parts) === groupAmountCents;

  const canConfirm =
    // != null: FDatePicker sets the value to undefined (not null) on clear
    formik.values.receiptDate != null &&
    !formik.isSubmitting &&
    !analysis.analyzing &&
    partsSumMatches &&
    !hasZeroAmountPart(formik.values.parts) &&
    !hasArchivedSubcategoryPart(
      formik.values.parts,
      subcategoriesData?.data ?? []
    ) &&
    !hasArchivedTaxPart(formik.values.parts, taxCodesData?.data ?? []) &&
    (formik.values.receipt_not_available ||
      formik.values.receiptImageIds.length > 0);

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      PaperProps={{
        sx: {
          width: '90vw',
          height: '90vh',
          maxWidth: '90vw',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <span>{t('receipts.unassociatedPliant.assignExpense.title')}</span>
          {description && (
            <Tooltip title={description} arrow>
              <Box
                component="span"
                sx={{ display: 'inline-flex', cursor: 'help' }}
              >
                <Iconify icon="eva:info-outline" fontSize={20} />
              </Box>
            </Tooltip>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <Stack
          ref={(node: HTMLDivElement | null) => {
            uploadReceipt.drop(node);
          }}
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{ flex: 1, minHeight: 0 }}
        >
          <Box
            sx={{
              flex: '0 0 60%',
              display: 'flex',
              overflow: 'hidden',
              minWidth: 0,
              minHeight: 0,
            }}
          >
            <UploadReceiptCard
              formik={formik}
              uploadReceipt={uploadReceipt}
              disabled={false}
              allowWithoutImage={
                !tenantInfo?.data?.data?.receipt_images_required
              }
            />
          </Box>

          <Stack
            spacing={2}
            sx={{
              flex: '1 1 40%',
              minWidth: 0,
              overflow: 'auto',
              minHeight: 0,
            }}
          >
            <Stack spacing={1.5}>
              <ContextRow
                label={t('accounting.expenses.dialog.context.amount')}
                value={fCurrencyCents(groupAmountCents)}
              />
              <ContextRow
                label={t('accounting.expenses.dialog.context.merchant')}
                value={merchantName ?? '--'}
              />
              {pliantTransactionCreatedAt && (
                <ContextRow
                  label={t('accounting.expenses.dialog.context.pliant-date')}
                  value={fDate(
                    new Date(pliantTransactionCreatedAt),
                    YEAR_MONTH_DAY_HOUR_MINUTE_SHORT_FORMAT
                  )}
                />
              )}
              {cardholderName && (
                <ContextRow
                  label={t('accounting.expenses.dialog.context.cardholder')}
                  value={cardholderName}
                />
              )}
            </Stack>
            <Divider />

            {analysis.analyzing && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  {t('accounting.expenses.dialog.analyzing-receipt')}
                </Typography>
              </Stack>
            )}

            <FDatePicker
              name="receiptDate"
              formik={formik}
              label={t('receipts.unassociatedPliant.assignExpense.receiptDate')}
              disabled={analysis.analyzing}
              maxDate={endOfToday()}
              InputProps={
                analysis.receiptDateAutofilled
                  ? { startAdornment: <ReceiptAutofillAdornment /> }
                  : undefined
              }
            />

            <FTextInput
              name="receiptNumber"
              formik={formik}
              label={t(
                'receipts.unassociatedPliant.assignExpense.receiptNumber'
              )}
              fullWidth
              disabled={analysis.analyzing || formik.isSubmitting}
              InputProps={
                analysis.receiptNumberAutofilled
                  ? { startAdornment: <ReceiptAutofillAdornment /> }
                  : undefined
              }
            />

            <FTextInput
              name="comment"
              formik={formik}
              label={t('receipts.unassociatedPliant.assignExpense.comment')}
              fullWidth
              multiline
              minRows={2}
            />

            <ExpensePartsEditor
              parts={formik.values.parts}
              onChange={(parts) => formik.setFieldValue('parts', parts)}
              groupAmount={groupAmountCents}
              subcategories={subcategoriesData?.data ?? []}
              taxCodes={taxCodesData?.data ?? []}
              errorMessage={
                typeof formik.errors.parts === 'string' &&
                formik.errors.parts !== 'parts-sum-mismatch'
                  ? formik.errors.parts
                  : null
              }
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color="inherit"
          disabled={formik.isSubmitting}
        >
          {t('actions.cancel')}
        </Button>
        <Button
          onClick={() => formik.handleSubmit()}
          color="primary"
          variant="contained"
          disabled={!canConfirm}
        >
          {t('receipts.unassociatedPliant.assignExpense.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface ContextRowProps {
  label: string;
  value: React.ReactNode;
}

const ContextRow: FC<ContextRowProps> = ({ label, value }) => (
  <Stack direction="row" spacing={1} alignItems="baseline">
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110 }}>
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Stack>
);
